import React, { useState, useEffect } from 'react';
import { categoriasAPI } from '../services/api';
import Modal from '../components/Modal';

function Categorias() {
  const [categorias, setCategorias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [modalConfig, setModalConfig] = useState({});
  const [formData, setFormData] = useState({
    nome: '',
    faixas: [],
    pesoMin: '',
    pesoMax: ''
  });

  const faixasDisponiveis = ['Branca', 'Cinza', 'Amarela', 'Laranja', 'Verde', 'Azul', 'Roxa', 'Marrom', 'Preta'];

  useEffect(() => {
    loadCategorias();
  }, []);

  const loadCategorias = async () => {
    try {
      const response = await categoriasAPI.getAll();
      setCategorias(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Erro ao carregar categorias:', error);
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await categoriasAPI.update(editingId, formData);
        setModalConfig({
          type: 'success',
          title: 'Categoria Atualizada',
          message: 'As configurações da categoria foram atualizadas com sucesso.',
          onConfirm: () => {
            setModalOpen(false);
            loadCategorias();
            resetForm();
          }
        });
        setModalOpen(true);
      } else {
        await categoriasAPI.create(formData);
        setModalConfig({
          type: 'success',
          title: 'Categoria Cadastrada',
          message: 'Nova categoria adicionada com sucesso ao sistema.',
          onConfirm: () => {
            setModalOpen(false);
            loadCategorias();
            resetForm();
          }
        });
        setModalOpen(true);
      }
    } catch (error) {
      console.error('Erro ao salvar categoria:', error);
      setModalConfig({
        type: 'error',
        title: 'Erro ao Salvar',
        message: 'Não foi possível salvar as informações da categoria.\nVerifique os dados e tente novamente.',
        onConfirm: () => setModalOpen(false)
      });
      setModalOpen(true);
    }
  };

  const handleEdit = (categoria) => {
    setFormData(categoria);
    setEditingId(categoria.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    const categoria = categorias.find(c => c.id === id);
    setModalConfig({
      type: 'confirm',
      title: 'Confirmar Exclusão',
      message: `Deseja realmente excluir a categoria "${categoria?.nome}"?\n\nAtenção: Esta ação pode afetar alunos e chaves vinculadas a esta categoria.\n\nEsta ação não poderá ser desfeita.`,
      showCancel: true,
      onConfirm: async () => {
        try {
          await categoriasAPI.delete(id);
          loadCategorias();
          setModalConfig({
            type: 'success',
            title: 'Sucesso',
            message: 'Categoria excluída com sucesso!',
            onConfirm: () => setModalOpen(false)
          });
          setModalOpen(true);
        } catch (error) {
          console.error('Erro ao excluir categoria:', error);
          setModalConfig({
            type: 'error',
            title: 'Erro',
            message: 'Erro ao excluir categoria. Verifique se não há alunos ou chaves vinculadas.',
            onConfirm: () => setModalOpen(false)
          });
          setModalOpen(true);
        }
      }
    });
    setModalOpen(true);
  };

  const handleFaixaChange = (faixa) => {
    const faixas = [...formData.faixas];
    const index = faixas.indexOf(faixa);
    
    if (index > -1) {
      faixas.splice(index, 1);
    } else {
      faixas.push(faixa);
    }
    
    setFormData({...formData, faixas});
  };

  const resetForm = () => {
    setFormData({ nome: '', faixas: [], pesoMin: '', pesoMax: '' });
    setEditingId(null);
    setShowForm(false);
  };

  const categoriasFiltradas = categorias.filter(categoria => {
    return !searchTerm || 
      categoria.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
      categoria.faixas.some(f => f.toLowerCase().includes(searchTerm.toLowerCase()));
  });

  if (loading) return <div className="loading">Carregando...</div>;

  return (
    <div>
      <div className="page-header">
        <h1>Gerenciamento de Categorias</h1>
        <p>Configure as categorias de competição</p>
      </div>

      <div style={{ marginBottom: '20px' }}>
        <button 
          className="btn btn-primary" 
          onClick={() => setShowForm(!showForm)}
        >
          {showForm ? 'Cancelar' : '+ Nova Categoria'}
        </button>
      </div>

      {!showForm && (
        <div className="search-filters-container" style={{ marginBottom: '20px' }}>
          <div className="search-box">
            <input
              type="text"
              placeholder="🔍 Buscar por nome ou faixa..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
            {searchTerm && (
              <button 
                className="clear-search"
                onClick={() => setSearchTerm('')}
                title="Limpar busca"
              >
                ✕
              </button>
            )}
          </div>
          <div className="pagination">
            <span>Mostrando <strong>{categoriasFiltradas.length}</strong> de <strong>{categorias.length}</strong> categorias</span>
          </div>
        </div>
      )}

      {showForm && (
        <div className="form-container" style={{ marginBottom: '30px' }}>
          <h2>{editingId ? 'Editar Categoria' : 'Nova Categoria'}</h2>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Nome *</label>
              <input
                type="text"
                value={formData.nome}
                onChange={(e) => setFormData({...formData, nome: e.target.value})}
                required
              />
            </div>

            <div className="form-group">
              <label>Faixas Permitidas *</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginTop: '10px' }}>
                {faixasDisponiveis.map(faixa => (
                  <label key={faixa} style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <input
                      type="checkbox"
                      checked={formData.faixas.includes(faixa)}
                      onChange={() => handleFaixaChange(faixa)}
                    />
                    {faixa}
                  </label>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label>Peso Mínimo (kg) *</label>
              <input
                type="number"
                step="0.1"
                value={formData.pesoMin}
                onChange={(e) => setFormData({...formData, pesoMin: e.target.value})}
                required
              />
            </div>

            <div className="form-group">
              <label>Peso Máximo (kg) *</label>
              <input
                type="number"
                step="0.1"
                value={formData.pesoMax}
                onChange={(e) => setFormData({...formData, pesoMax: e.target.value})}
                required
              />
            </div>

            <div className="actions">
              <button type="submit" className="btn btn-success">
                {editingId ? 'Atualizar' : 'Cadastrar'}
              </button>
              <button type="button" className="btn btn-secondary" onClick={resetForm}>
                Cancelar
              </button>
            </div>
          </form>
        </div>
      )}

      {!showForm && (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Nome</th>
                <th>Faixas</th>
                <th>Peso Mínimo</th>
                <th>Peso Máximo</th>
                <th>Ações</th>
              </tr>
            </thead>
          <tbody>
            {categoriasFiltradas.map(categoria => (
              <tr key={categoria.id}>
                <td>{categoria.nome}</td>
                <td>{categoria.faixas.join(', ')}</td>
                <td>{categoria.pesoMin} kg</td>
                <td>{categoria.pesoMax} kg</td>
                <td>
                  <div className="actions">
                    <button 
                      className="btn-primary" 
                      onClick={() => handleEdit(categoria)}
                      style={{ marginRight: '8px' }}
                    >
                      Editar
                    </button>
                    <button 
                      className="btn-danger-ghost" 
                      onClick={() => handleDelete(categoria.id)}
                    >
                      Excluir
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}
      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        {...modalConfig}
      />
    </div>
  );
}

export default Categorias;
